// Editor for `Model.spendPerFte`.

import { Plus, X } from 'lucide-react';
import { modelCurrency } from '@rc/engine';
import type { Model, SpendPerFteRow } from '@rc/engine/types';

interface Props {
  model: Model;
  onChange: (m: Model) => void;
}

export default function SpendPerFteTable({ model, onChange }: Props) {
  const rows = model.spendPerFte || [];
  const channels = model.channels || [];
  const disciplines = model.disciplines || [];
  const base = modelCurrency(model).base;

  const update = (next: SpendPerFteRow[]) => onChange({ ...model, spendPerFte: next });
  const updateRow = (i: number, patch: Partial<SpendPerFteRow>) => {
    const next = [...rows];
    next[i] = { ...next[i], ...patch };
    update(next);
  };
  const remove = (i: number) => update(rows.filter((_, j) => j !== i));
  const add = () =>
    update([
      ...rows,
      {
        disciplineId: disciplines[0]?.id || 'strategy_planning',
        channelId: 'all',
        ratio: 10_000_000,
        basis: 'totalBudget',
      },
    ]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-slate-500">
        Spend per FTE drives the Stage-1 total-FTE-per-cell calculation:{' '}
        <span className="font-mono text-slate-700">FTE = drivingBudget / ratio</span>. Use channel{' '}
        <span className="font-mono">all</span> to apply a single ratio across an entire discipline.
      </p>
      <div className="rounded-lg border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
            <tr>
              <th className="text-left px-3 py-2">Discipline</th>
              <th className="text-left px-3 py-2">Channel</th>
              <th className="text-right px-3 py-2">Ratio ({base} / FTE)</th>
              <th className="text-left px-3 py-2">Basis</th>
              <th className="text-left px-3 py-2">Custom basis key</th>
              <th className="text-left px-3 py-2">Note</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-100">
                <td className="px-3 py-1.5">
                  <select
                    className="input py-1 px-2"
                    value={r.disciplineId}
                    onChange={(e) => updateRow(i, { disciplineId: e.target.value })}
                  >
                    {disciplines.map((d) => (
                      <option key={d.id} value={d.id}>
                        {d.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <select
                    className="input py-1 px-2"
                    value={r.channelId}
                    onChange={(e) => updateRow(i, { channelId: e.target.value })}
                  >
                    <option value="all">All channels</option>
                    {channels.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </td>
                <td className="px-3 py-1.5 text-right">
                  <input
                    type="number"
                    className="input py-1 px-2 w-32 text-right tabular-nums"
                    value={r.ratio ?? ''}
                    onChange={(e) =>
                      updateRow(i, { ratio: e.target.value === '' ? null : Number(e.target.value) })
                    }
                  />
                </td>
                <td className="px-3 py-1.5">
                  <select
                    className="input py-1 px-2"
                    value={r.basis}
                    onChange={(e) =>
                      updateRow(i, { basis: e.target.value as SpendPerFteRow['basis'] })
                    }
                  >
                    <option value="totalBudget">Total budget</option>
                    <option value="channelBudget">Channel budget</option>
                    <option value="custom">Custom</option>
                  </select>
                </td>
                <td className="px-3 py-1.5">
                  <input
                    className="input py-1 px-2 w-44 font-mono text-xs"
                    placeholder="e.g. programmatic+digital_direct"
                    value={r.customBasisKey || ''}
                    onChange={(e) => updateRow(i, { customBasisKey: e.target.value || undefined })}
                    disabled={r.basis !== 'custom'}
                  />
                </td>
                <td className="px-3 py-1.5">
                  <input
                    className="input py-1 px-2 w-44"
                    value={r.note || ''}
                    onChange={(e) => updateRow(i, { note: e.target.value || undefined })}
                  />
                </td>
                <td className="px-3 py-1.5 text-right">
                  <button
                    type="button"
                    onClick={() => remove(i)}
                    className="text-slate-400 hover:text-rose-500"
                  >
                    <X size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={add} className="btn-secondary inline-flex items-center gap-1">
        <Plus size={14} /> Add row
      </button>
    </div>
  );
}
